<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Recycle Bin"
      subtitle="Soft-deleted employees can be restored or permanently purged"
      icon="pi-trash"
    >
      <template #actions>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadItems"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !items.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          :show-inactive-toggle="false"
          search-placeholder="Search recycle bin..."
          :loading="loading"
        />

        <div v-if="!items.length" class="settings-org-section__empty">
          <i class="pi pi-trash settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
        </div>

        <DataTable
          v-else
          :value="items"
          size="small"
          class="settings-recycle-table"
          dataKey="user_id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField || undefined"
          :sort-order="sortOrderPrime"
          @sort="onSort"
        >
          <Column field="name" header="Employee" sortable />
          <Column field="department_name" header="Department" sortable>
            <template #body="{ data }">
              {{ data.department_name || '—' }}
            </template>
          </Column>
          <Column field="position_name" header="Position" sortable>
            <template #body="{ data }">
              {{ data.position_name || '—' }}
            </template>
          </Column>
          <Column field="deleted_at" header="Deleted" sortable>
            <template #body="{ data }">
              <span class="text-xs whitespace-nowrap">{{ data.deleted_at }}</span>
            </template>
          </Column>
          <Column field="deleted_by_name" header="Deleted by" sortable>
            <template #body="{ data }">
              {{ data.deleted_by_name || '—' }}
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="flex flex-wrap gap-1">
                <Button
                  label="Restore"
                  icon="pi pi-replay"
                  size="small"
                  severity="success"
                  outlined
                  :loading="mutatingId === data.user_id && mutatingAction === 'restore'"
                  :disabled="mutatingId != null"
                  @click="confirmRestore(data)"
                />
                <Button
                  v-if="canPurge"
                  label="Purge"
                  icon="pi pi-times"
                  size="small"
                  severity="danger"
                  text
                  :loading="mutatingId === data.user_id && mutatingAction === 'purge'"
                  :disabled="mutatingId != null"
                  @click="confirmPurge(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-if="total > items.length" class="flex justify-center mt-3">
          <Button
            label="Load more"
            icon="pi pi-chevron-down"
            size="small"
            severity="secondary"
            outlined
            :loading="loadingMore"
            @click="loadMore"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuth } from '@/composables/useAuth';
import {
  fetchRecycleBin,
  purgeFromRecycle,
  restoreFromRecycle,
} from '@/api/employeesApi';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';

const confirm = useConfirm();
const toast = useToast();
const { isSuperAdmin, hasPermission } = useAuth();

const items = ref([]);
const total = ref(0);
const page = ref(1);
const limit = 25;
const loading = ref(false);
const loadingMore = ref(false);
const error = ref(null);
const searchQuery = ref('');
const sortField = ref(null);
const sortOrder = ref(null);
const mutatingId = ref(null);
const mutatingAction = ref(null);

let searchDebounce = null;

const canPurge = computed(
  () => isSuperAdmin.value || hasPermission('employees.delete.global')
);

const sortOrderPrime = computed(() => (
  sortField.value ? (sortOrder.value === 'ASC' ? 1 : -1) : undefined
));

const countLabel = computed(() => {
  const n = total.value;
  return `${n} deleted employee${n === 1 ? '' : 's'}`;
});

const emptyMessage = computed(() =>
  searchQuery.value.trim()
    ? 'No recycle bin entries match your search.'
    : 'Recycle bin is empty.'
);

function buildFetchParams(nextPage) {
  return {
    page: nextPage,
    limit,
    search: searchQuery.value.trim(),
    sortField: sortField.value || 'deleted_at',
    sortOrder: sortOrder.value || 'DESC',
  };
}

async function loadItems() {
  loading.value = true;
  error.value = null;
  page.value = 1;
  try {
    const data = await fetchRecycleBin(buildFetchParams(1));
    items.value = data.items;
    total.value = data.total;
  } catch (err) {
    error.value = err?.message || 'Failed to load recycle bin.';
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || items.value.length >= total.value) return;
  loadingMore.value = true;
  try {
    const nextPage = page.value + 1;
    const data = await fetchRecycleBin(buildFetchParams(nextPage));
    items.value = items.value.concat(data.items);
    total.value = data.total;
    page.value = nextPage;
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Load failed',
      detail: err?.message || 'Could not load more entries.',
      life: 3500,
    });
  } finally {
    loadingMore.value = false;
  }
}

function onSort(event) {
  if (!event?.sortField || event?.sortOrder === 0 || event?.sortOrder == null) {
    sortField.value = null;
    sortOrder.value = null;
  } else {
    sortField.value = event.sortField || 'deleted_at';
    sortOrder.value = event.sortOrder === 1 ? 'ASC' : 'DESC';
  }
  loadItems();
}

async function handleRestore(row) {
  mutatingId.value = row.user_id;
  mutatingAction.value = 'restore';
  try {
    await restoreFromRecycle({ user_id: row.user_id });
    items.value = items.value.filter((item) => item.user_id !== row.user_id);
    total.value = Math.max(0, total.value - 1);
    toast.add({
      severity: 'success',
      summary: 'Restored',
      detail: `${row.name} was restored to the active roster.`,
      life: 3000,
    });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Restore failed',
      detail: err?.message || 'Could not restore employee.',
      life: 3500,
    });
  } finally {
    mutatingId.value = null;
    mutatingAction.value = null;
  }
}

async function handlePurge(row) {
  mutatingId.value = row.user_id;
  mutatingAction.value = 'purge';
  try {
    await purgeFromRecycle({ user_id: row.user_id });
    items.value = items.value.filter((item) => item.user_id !== row.user_id);
    total.value = Math.max(0, total.value - 1);
    toast.add({
      severity: 'success',
      summary: 'Purged',
      detail: `${row.name} was permanently removed.`,
      life: 3000,
    });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Purge failed',
      detail: err?.message || 'Could not purge employee.',
      life: 4000,
    });
  } finally {
    mutatingId.value = null;
    mutatingAction.value = null;
  }
}

function confirmRestore(row) {
  confirm.require({
    message: `Restore ${row.name} to the active roster?`,
    header: 'Restore employee',
    icon: 'pi pi-replay',
    accept: () => handleRestore(row),
  });
}

function confirmPurge(row) {
  confirm.require({
    message: `Permanently purge ${row.name}? This cannot be undone.`,
    header: 'Purge employee',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => handlePurge(row),
  });
}

watch(searchQuery, () => {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    loadItems();
  }, 300);
});

onMounted(loadItems);
</script>
